import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

// NOOVI (Fase 60) — espelha `filterByUnattended` para a lista "Sem resposta".
//
// Sem isto a conversa respondida só sai da lista no próximo recarregamento: o
// filtro é do servidor, e a store recebe a atualização da conversa mas não teria
// como reavaliar a pertinência dela à lista.
export const filterByAwaitingReply = (
  shouldFilter,
  conversationType,
  awaitingSince,
  chatStatus
) => {
  if (conversationType !== 'awaiting_reply') return shouldFilter;

  // Fecha para o lado seguro quando o campo não vem.
  //
  // Isso acontece de verdade num deploy rolante: o frontend novo pergunta por
  // `conversation_type=awaiting_reply` a um pod antigo, cujo `case` não tem esse
  // ramo E não tem `else` — ele devolve o escopo BASE, sem filtro nenhum, e sem
  // o campo. Confiar no servidor ali encheria a lista "Sem resposta" com TODAS
  // as conversas da conta, rotuladas como se estivessem esperando. Lista vazia
  // se explica sozinha; lista errada, não.
  // Adiada e resolvida o servidor exclui pelo STATUS, não pela marcação. Sem
  // espelhar isso, adiar uma conversa por tempo real a deixaria na lista até o
  // próximo carregamento — a marcação continua lá, é o status que muda.
  if (chatStatus === 'snoozed' || chatStatus === 'resolved') return false;

  return !!awaitingSince && shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const { inboxId, status, labels = [], teamId, conversationType } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
    awaiting_human_reply_since: awaitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );
  shouldFilter = filterByAwaitingReply(
    shouldFilter,
    conversationType,
    awaitingSince,
    chatStatus
  );

  return shouldFilter;
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

/**
 * Frontend defense-in-depth for team-based conversation visibility.
 *
 * When the account setting `team_based_conversation_visibility` is enabled,
 * a conversation assigned to a team should only be visible to members of
 * that team (plus administrators and the assignee). The backend already
 * enforces this on REST listing and realtime; this guard prevents a
 * conversation that arrives over the websocket from leaking into the store.
 *
 * @param {Object} conversation - The conversation object to evaluate
 * @param {string} role - The current user's role (administrator, agent, etc.)
 * @param {number|string} currentUserId - The ID of the current user
 * @param {boolean} teamVisibilityEnabled - Whether the account setting is on
 * @param {Array<number>} myTeamIds - Team ids the current user is a member of
 * @returns {boolean} - Whether the conversation should be added to the store
 */
export const shouldAddConversationForTeamVisibility = (
  conversation,
  role,
  currentUserId,
  teamVisibilityEnabled,
  myTeamIds = []
) => {
  // Setting disabled (default) → behave exactly as before
  if (!teamVisibilityEnabled) {
    return true;
  }

  const team = conversation.meta?.team;
  // No team assigned → visibility restriction does not apply
  if (!team?.id) {
    return true;
  }

  // Administrators always see every conversation
  if (role === 'administrator') {
    return true;
  }

  // Member of the assigned team
  if (myTeamIds.includes(team.id)) {
    return true;
  }

  // The assignee always sees their own conversation
  const assigneeId = conversation.meta?.assignee?.id;
  if (assigneeId && assigneeId === currentUserId) {
    return true;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  priority_desc_created_at_asc: ['sortOnPriorityCreatedAt', 'desc'],
  unread: ['sortOnUnread', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at) ||
    getSortOrderFunction(sortDirection)(a.id, b.id),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    const priorityDiff = getSortOrderFunction(sortDirection)(p1, p2);
    if (priorityDiff !== 0) return priorityDiff;

    return sortDescending(a.last_activity_at, b.last_activity_at);
  },

  sortOnPriorityCreatedAt: (a, b) => {
    const DEFAULT_FOR_NULL = 0;
    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    if (p1 !== p2) return p2 - p1;
    return a.created_at - b.created_at;
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortAscending(a.created_at, b.created_at);
      }
      return a.waiting_since ? -1 : 1;
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },

  sortOnUnread: (a, b) => {
    const unreadCountDiff = (b.unread_count || 0) - (a.unread_count || 0);
    if (unreadCountDiff !== 0) return unreadCountDiff;

    return (b.last_activity_at || 0) - (a.last_activity_at || 0);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};
